import { required, helpers, minLength, maxLength, sameAs } from '@vuelidate/validators';
import {
    isValidEmail,
    isValidFullName,
    isValidName,
    isValidKana,
    isValidZipCode,
    isValidTel,
    isValidYear,
    isValidMonth,
    isValidDay,
    isValidAddress1,
    isValidAddress2,
    isValidAddress3,
    isValidSex, isValidBirthday,
    isValidForeignAddress
} from './common';
import { computed } from 'vue';

export function getCreateUserRules(userStore) {
    return computed(() => {
        return {
            email: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidEmail: helpers.withMessage('ERROR_EMAIL_INVALID', isValidEmail),
                minLength: minLength(1),
                maxLength: maxLength(255)
            },
            password: {
                required: helpers.withMessage('ERROR_REQUIRED', required)
            },
            password_confirmation: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                sameAs: sameAs(userStore.registrationParams.password)
            },
            first_name: {
                required: helpers.withMessage('ERROR_REQUIRED_FIRST_NAME', required),
                isValidFullName: helpers.withMessage('ERROR_FIRST_NAME_INPUT_INVALID', isValidFullName),
            },
            last_name: {
                required: helpers.withMessage('ERROR_REQUIRED_LAST_NAME', required),
                isValidName: helpers.withMessage('ERROR_LAST_NAME_INPUT_INVALID', isValidName),
            },
            first_name_kana: {
                required: helpers.withMessage('ERROR_REQUIRED_FIRST_NAME_KANA', required),
                isValidKana: helpers.withMessage('ERROR_FIRST_NAME_KANA_INPUT_INVALID', isValidKana),
            },
            last_name_kana: {
                required: helpers.withMessage('ERROR_REQUIRED_LAST_NAME_KANA', required),
                isValidKana: helpers.withMessage('ERROR_LAST_NAME_KANA_INPUT_INVALID', isValidKana),
            },
            zip_code: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidZipCode: helpers.withMessage('ERROR_ZIP_CODE_INPUT_INVALID', isValidZipCode)
            },
            country: {
                required: helpers.withMessage('ERROR_REQUIRED', required)
            },
            address_1: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidAddress1: helpers.withMessage('ERROR_ADDRESS_1_INPUT_INVALID', isValidAddress1),
                minLength: minLength(1),
                maxLength: maxLength(100)
            },
            address_2: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidAddress2: helpers.withMessage('ERROR_ADDRESS_2_INPUT_INVALID', isValidAddress2)
            },
            address_3: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidAddress3: helpers.withMessage('ERROR_ADDRESS_3_INPUT_INVALID', isValidAddress3)
            },
            tel: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                minLength: minLength(10),
                maxLength: maxLength(20),
                isValidTel: helpers.withMessage('ERROR_TEL_INPUT_INVALID', isValidTel),
            },
            year: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidYear: helpers.withMessage('ERROR_YEAR_INPUT_INVALID', isValidYear)
            },
            month: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidMonth: helpers.withMessage('ERROR_MONTH_INPUT_INVALID', isValidMonth)
            },
            day: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidDay: helpers.withMessage('ERROR_DAY_INPUT_INVALID', isValidDay)
            },
            sex: {
                required: helpers.withMessage('ERROR_REQUIRED', required),
                isValidSex: helpers.withMessage('ERROR_SEX_INPUT_INVALID', isValidSex)
            },
        }
    })
}

export function getRegisterVerifyRules(userStore, t) {
    return computed(() => {
        return {
            email: {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidEmail: helpers.withMessage(t('ERROR_EMAIL_INVALID'), isValidEmail),
                minLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MIN_LENGTH', { min: $params.min }),
                    minLength(1)
                ),
                maxLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MAX_LENGTH', { max: $params.max }),
                    maxLength(255)
                )
            },
            password: {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                minLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MIN_LENGTH', { min: $params.min }),
                    minLength(8)
                ),
                maxLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MAX_LENGTH', { max: $params.max }),
                    maxLength(16)
                )
            },
            password_confirmation: {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                sameAs: helpers.withMessage(t('ERROR_PASSWORD_CONFIRMATION_MISMATCH'), sameAs(userStore.registerVerifyParams.password))
            }
        }
    })
}

export function getEditAccountRules(userStore, t) {
    return computed(() => {
        return {
            full_name: !userStore.isOverseasUser ? {} : {
                required: helpers.withMessage(t('ERROR_REQUIRED_FULL_NAME'), required),
                isValidFullName: helpers.withMessage(t('ERROR_FULL_NAME_INPUT_INVALID'), isValidFullName),
            },
            first_name: userStore.isOverseasUser ? {} : {
                required: helpers.withMessage(t('ERROR_REQUIRED_FIRST_NAME'), required),
                isValidName: helpers.withMessage(t('ERROR_FIRST_NAME_INPUT_INVALID'), isValidName),
            },
            last_name: userStore.isOverseasUser ? {} : {
                required: helpers.withMessage(t('ERROR_REQUIRED_LAST_NAME'), required),
                isValidName: helpers.withMessage(t('ERROR_LAST_NAME_INPUT_INVALID'), isValidName),
            },
            first_name_kana: userStore.isOverseasUser ? {} : {
                required: helpers.withMessage(t('ERROR_REQUIRED_FIRST_NAME_KANA'), required),
                isValidKana: helpers.withMessage(t('ERROR_FIRST_NAME_KANA_INPUT_INVALID'), isValidKana),
            },
            last_name_kana: userStore.isOverseasUser ? {} : {
                required: helpers.withMessage(t('ERROR_REQUIRED_LAST_NAME_KANA'), required),
                isValidKana: helpers.withMessage(t('ERROR_LAST_NAME_KANA_INPUT_INVALID'), isValidKana),
            },
            sex: {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidSex: helpers.withMessage(t('ERROR_SEX_INPUT_INVALID'), isValidSex)
            },
            birthday: {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidBirthday: helpers.withMessage(t('ERROR_BIRTHDAY_INPUT_INVALID'), isValidBirthday)
            },
            tel: {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidTel: helpers.withMessage(t('ERROR_TEL_INPUT_INVALID'), isValidTel),
                minLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MIN_LENGTH', { min: $params.min }),
                    minLength(10)
                ),
                maxLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MAX_LENGTH', { max: $params.max }),
                    maxLength(20)
                )
            },
            country: !userStore.isOverseasUser ? {} : {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required)
            },
            zip_code: {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                maxLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MAX_LENGTH', { max: $params.max }),
                    maxLength(7)
                )
            },
            address_1: userStore.isOverseasUser ? {} : {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidAddress1: helpers.withMessage(t('ERROR_ADDRESS_1_INPUT_INVALID'), isValidAddress1),
                minLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MIN_LENGTH', { min: $params.min }),
                    minLength(1)
                ),
                maxLength: helpers.withMessage(
                    ({ $params }) => t('ERROR_MAX_LENGTH', { max: $params.max }),
                    maxLength(100)
                )
            },
            address_2: userStore.isOverseasUser ? {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidForeignAddress: helpers.withMessage(
                    t('ERROR_ADDRESS_INVALID'),
                    isValidForeignAddress
                ),
            } : {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidAddress2: helpers.withMessage(t('ERROR_ADDRESS_2_INPUT_INVALID'), isValidAddress2)
            },
            address_3: userStore.isOverseasUser ? {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidForeignAddress: helpers.withMessage(
                    t('ERROR_ADDRESS_INVALID'),
                    isValidForeignAddress
                ),
            } : {
                required: helpers.withMessage(t('ERROR_REQUIRED'), required),
                isValidAddress3: helpers.withMessage(t('ERROR_ADDRESS_3_INPUT_INVALID'), isValidAddress3)
            },
        }
    })
}